import { useMutation, useQuery } from '@tanstack/react-query';
import {
  fetchSettingsStatus,
  testTelegramConnection,
  type SettingsStatus,
} from '../../api';
import { qk } from '../../api/queryKeys';
import { errorMessage } from '../../lib/api';
import { Callout } from '../../components/Callout';
import { Spinner } from '../../components/Spinner';
import { LoadingState, ErrorState } from '../../components/States';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { SettingsSection, SecretField, type SecretSpec } from './common';

const TELEGRAM_BOT_TOKEN_SECRET: SecretSpec = {
  key: 'TELEGRAM_BOT_TOKEN',
  title: 'Telegram bot token',
  description:
    'Create a bot with @BotFather on Telegram and paste the token it gives you.',
  placeholder: '123456789:AAExample-Token',
  removeConsequence:
    'Telegram notifications will stop until a new one is set.',
};

const TELEGRAM_CHAT_ID_SECRET: SecretSpec = {
  key: 'TELEGRAM_CHAT_ID',
  title: 'Telegram chat ID',
  description:
    'The chat (or channel) the bot should message. Message the bot, then check https://api.telegram.org/bot<token>/getUpdates for the chat id.',
  placeholder: '-1001234567890',
  removeConsequence:
    'Telegram notifications will stop until a new one is set.',
};

function TelegramTestButton() {
  const test = useMutation({ mutationFn: testTelegramConnection });

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-ink">Test connection</p>
          <p className="mt-0.5 text-xs text-ink-muted">
            Send a test message to verify your bot token and chat ID are correct.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary whitespace-nowrap"
          disabled={test.isPending}
          onClick={() => test.mutate()}
        >
          {test.isPending && <Spinner className="h-3.5 w-3.5" />}
          Send test message
        </button>
      </div>
      {test.isSuccess && (
        <Callout tone="success" className="mt-3">
          Test message sent — check Telegram.
        </Callout>
      )}
      {test.isError && (
        <Callout tone="danger" className="mt-3">
          {errorMessage(test.error, 'Could not send test message')}
        </Callout>
      )}
    </div>
  );
}

export function NotificationsSettingsSection() {
  useDocumentTitle('Settings · Notifications');

  const { data, isLoading, isError, refetch } = useQuery<SettingsStatus>({
    queryKey: qk.settings,
    queryFn: fetchSettingsStatus,
  });

  if (isLoading) return <LoadingState label="Loading settings…" />;
  if (isError) return <ErrorState message="Could not load settings" onRetry={() => refetch()} />;

  const telegramBotConfigured = Boolean(data?.[TELEGRAM_BOT_TOKEN_SECRET.key]);
  const telegramChatConfigured = Boolean(data?.[TELEGRAM_CHAT_ID_SECRET.key]);

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Telegram"
        description="Get a Telegram message when a new version of a prototype is live (with its release notes), when a prototype goes to sleep after a day without visitors, when the platform gives up on an issue, and once a day per project with what shipped. Nothing else is pushed: everything an agent does is on the board. This bot/chat is specific to the current workspace."
      >
        <SecretField
          spec={TELEGRAM_BOT_TOKEN_SECRET}
          configured={telegramBotConfigured}
        />
        <SecretField
          spec={TELEGRAM_CHAT_ID_SECRET}
          configured={telegramChatConfigured}
        />
        {telegramBotConfigured && telegramChatConfigured && <TelegramTestButton />}
      </SettingsSection>

      <SettingsSection
        title="Browser notifications"
        description="Desktop notifications for Product Manager chat replies are turned on per browser from a project's Chat page (“Notify me when the PM replies”). They only arrive while an aiai·loop tab is open."
      >
        {null}
      </SettingsSection>
    </div>
  );
}
